import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import useAdminAuthCheck from "../utils/use-admin-auth-check";
import {
  Category,
  People,
  DocumentText,
  Card,
  DirectInbox,
  HamburgerMenu,
  CloseCircle,
  ArrowDown2,
  SearchNormal1,
  Setting2,
  User,
  SecuritySafe,
  DocumentCopy,
  ExportCurve,
  MessageText1,
} from "iconsax-reactjs";
import { Dropdown, Menu } from "antd";
import AppLogo from "../components/Logo/AppLogo";
import { useSelector } from "react-redux";
import { canViewAdminArea, getAdminRole } from "../views/Admin/adminUtils";

const AdminContainer = () => {
  useAdminAuthCheck();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((store) => store.auth);
  const adminRole = getAdminRole(user);

  const navigation = [
    { name: "Overview", href: "/admin", icon: Category },
    { name: "Users", href: "/admin/users", icon: People },
    { name: "Subscribers", href: "/admin/subscribers", icon: DirectInbox },
    { name: "Users", href: "/admin/users", icon: People, area: "users" },
    {
      name: "Pending Deletions",
      href: "/admin/users/pending-deletion",
      icon: SecuritySafe,
      area: "pendingDeletions",
    },
    { name: "Wills", href: "/admin/wills", icon: DocumentText, area: "wills" },
    {
      name: "Designations",
      href: "/admin/designations",
      icon: User,
      area: "designations",
    },
    {
      name: "Transactions",
      href: "/admin/transactions",
      icon: Card,
      area: "transactions",
    },
    {
      name: "Contact Us",
      href: "/admin/contact-us",
      icon: MessageText1,
      area: "contactMessages",
    },
    { name: "Documents", href: "/admin/documents", icon: DocumentText },
    { name: "Plan", href: "/admin/plans", icon: Card, area: "plans" },
    {
      name: "Audit Logs",
      href: "/admin/audit-logs",
      icon: DocumentCopy,
      area: "auditLogs",
    },
  ].filter((item) => !item.area || canViewAdminArea(adminRole, item.area));

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/admin") return "Overview";
    if (path === "/admin/users") return "Users";
    if (path === "/admin/subscribers") return "Subscribers";
    if (path === "/admin/users/pending-deletion") return "Pending Deletions";
    if (path === "/admin/wills") return "Wills";
    if (path === "/admin/transactions") return "Transactions";
    if (path === "/admin/contact-us") return "Contact Us";
    if (path === "/admin/audit-logs") return "Audit Logs";
    if (path === "/admin/documents") return "Documents";
    if (path === "/admin/plans") return "Plans";
    return "Admin";
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-56 bg-white border-r border-gray-100 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto flex flex-col overflow-y-auto flex-shrink-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-5">
          <AppLogo className="w-32 mb-8" />
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <CloseCircle size="20" />
          </button>
        </div>

        <nav className="px-3 space-y-2 flex-1">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={true}
              className={({ isActive }) =>
                `flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-gray-900 text-white hover:text-white "
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`w-7 h-7 rounded-md flex items-center justify-center mr-3 ${
                      isActive ? "bg-[#E2FD7E]" : ""
                    }`}
                  >
                    <item.icon
                      size="18"
                      className={`${isActive ? "text-gray-900" : "text-gray-500"}`}
                    />
                  </div>
                  {item.name}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <header className="sticky top-0 z-30 bg-white border-b border-dashed border-gray-200">
          <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center space-x-4">
              <button
                className="lg:hidden text-gray-600 hover:text-gray-900"
                onClick={() => setSidebarOpen(true)}
              >
                <HamburgerMenu size="24" />
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-[#bedf42] text-xl">✱</span>
                <h1 className="text-lg font-semibold text-gray-900">
                  {getPageTitle()}
                </h1>
              </div>
            </div>

            <div className="hidden md:flex items-center flex-1 max-w-xl mx-4 lg:mx-8">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search users, and documents"
                  className="w-full pl-4 pr-10 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                />
                <SearchNormal1
                  size="20"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-full">
                <SearchNormal1 size="20" />
              </button>

              <div>
                <Dropdown
                  trigger={["click"]}
                  overlay={
                    <Menu className="rounded-2xl border-none shadow-2xl p-2 min-w-[220px]">
                      <Menu.Item
                        key="settings-title"
                        disabled
                        className="!cursor-default !bg-transparent !py-4 px-4 text-xs font-semibold text-gray-300 uppercase tracking-widest border-b border-gray-50 mb-2"
                      >
                        Settings
                      </Menu.Item>
                      <Menu.Item
                        key="account"
                        icon={<User size="18" />}
                        className="!py-3 rounded-xl hover:!bg-[#f8f9f0] !text-gray-700 font-medium"
                      >
                        Account
                      </Menu.Item>
                      <Menu.Item
                        key="privacy"
                        icon={<SecuritySafe size="18" />}
                        className="!py-3 rounded-xl hover:!bg-[#f8f9f0] !text-gray-700 font-medium"
                      >
                        Privacy
                      </Menu.Item>
                      <Menu.Item
                        key="terms"
                        icon={<DocumentCopy size="18" />}
                        className="!py-3 rounded-xl hover:!bg-[#f8f9f0] !text-gray-700 font-medium"
                      >
                        Terms
                      </Menu.Item>
                      <Menu.Item
                        key="website"
                        icon={<ExportCurve size="18" />}
                        onClick={() => navigate("/")}
                        className="!py-3 rounded-xl hover:!bg-[#f8f9f0] !text-gray-700 font-medium border-t border-gray-50 mt-2"
                      >
                        Go to main website
                      </Menu.Item>
                    </Menu>
                  }
                  placement="bottomRight"
                >
                  <button className="flex items-center space-x-1.5 p-1.5 hover:bg-gray-50 rounded-lg transition-all group">
                    <Setting2
                      size="22"
                      className="text-gray-900 group-hover:rotate-45 transition-transform"
                    />
                    <ArrowDown2
                      size="14"
                      className="text-gray-400 group-hover:text-gray-600"
                    />
                  </button>
                </Dropdown>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminContainer;
